import type { Announcement } from '@/types/announcement';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';

// Generate print-friendly HTML for a single announcement
export function generatePrintHtml(
  announcement: Announcement, 
  relationshipLabel: string | undefined,
  isPremium: boolean,
  locale: string = 'en'
): string {
  const senderLabel = getSenderPerspectiveLabel(relationshipLabel);
  const dateLocale = locale === 'es' ? 'es-ES' : 'en-US';
  const publishedDate = announcement.published_at 
    ? new Date(announcement.published_at).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : new Date(announcement.created_at).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

  // Premium gets higher resolution, better spacing
  const margins = isPremium ? '2cm' : '1.5cm';
  const fontScale = isPremium ? '1.1' : '1';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>A message for ${announcement.recipient_name} | Say It NOW</title>
      <style>
        @page {
          size: A4;
          margin: ${margins};
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: calc(14px * ${fontScale});
          line-height: 1.8;
          color: #1a1a1a;
          max-width: 100%;
          margin: 0;
          padding: 0;
          background: white;
        }
        
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 2rem;
        }
        
        .header {
          text-align: center;
          margin-bottom: 3rem;
          padding-bottom: 2rem;
          border-bottom: 2px solid #e5e5e5;
        }
        
        .recipient-label {
          font-size: 0.75em;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #666;
          margin-bottom: 0.5rem;
        }
        
        .recipient-name {
          font-size: 2em;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }
        
        .relationship {
          font-size: 0.9em;
          color: #888;
          font-style: italic;
          margin-top: 0.5rem;
        }
        
        .photos {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin: 2rem 0;
        }
        
        .photo {
          max-width: 150px;
          max-height: 150px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .message {
          font-size: 1.1em;
          line-height: 2;
          white-space: pre-wrap;
          margin: 2rem 0;
          text-align: ${isPremium ? 'justify' : 'left'};
        }
        
        .footer {
          margin-top: 4rem;
          padding-top: 2rem;
          border-top: 1px solid #e5e5e5;
          text-align: center;
          color: #999;
          font-size: 0.8em;
        }
        
        .date {
          font-size: 0.85em;
          color: #888;
        }
        
        .branding {
          margin-top: 1rem;
          font-size: 0.75em;
        }
        
        ${isPremium ? `
          /* Premium enhancements */
          .container {
            padding: 3rem;
          }
          
          .header {
            border-bottom: 3px double #d4a574;
          }
          
          .recipient-name {
            color: #4a3728;
          }
          
          .message {
            padding: 0 1rem;
            border-left: 3px solid #d4a574;
          }
          
          .footer {
            border-top: 2px solid #d4a574;
          }
        ` : ''}
        
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <p class="recipient-label">A Living Announcement For</p>
          <h1 class="recipient-name">${announcement.recipient_name}</h1>
          <p class="relationship">From your ${senderLabel}</p>
        </div>
        
        ${announcement.photo_urls && announcement.photo_urls.length > 0 ? `
          <div class="photos">
            ${announcement.photo_urls.slice(0, 3).map(url => `
              <img src="${url}" alt="Photo" class="photo" />
            `).join('')}
          </div>
        ` : ''}
        
        <div class="message">
          ${announcement.message}
        </div>
        
        <div class="footer">
          <p class="date">${publishedDate}</p>
          <p class="branding">Created with Say It NOW — While They're Still Here</p>
        </div>
      </div>
    </body>
    </html>
  `;
}

// Generate print-friendly HTML for magazine view (multiple announcements)
export function generateMagazinePrintHtml(
  announcements: Announcement[],
  isPremium: boolean,
  locale: string = 'en'
): string {
  const margins = isPremium ? '1.5cm' : '1cm';
  const dateLocale = locale === 'es' ? 'es-ES' : 'en-US';
  
  const announcementPages = announcements.map((announcement, index) => {
    const publishedDate = announcement.published_at 
      ? new Date(announcement.published_at).toLocaleDateString(dateLocale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : new Date(announcement.created_at).toLocaleDateString(dateLocale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
    
    return `
      <div class="page ${index > 0 ? 'page-break' : ''}">
        <div class="page-header">
          <span class="page-number">Page ${index + 1}</span>
        </div>
        
        <div class="announcement">
          ${announcement.photo_urls?.[0] ? `
            <div class="photo-section">
              <img src="${announcement.photo_urls[0]}" alt="${announcement.recipient_name}" class="main-photo" />
            </div>
          ` : ''}
          
          <div class="content-section">
            <h2 class="recipient">${announcement.recipient_name}</h2>
            <p class="date">${publishedDate}</p>
            <p class="message">${announcement.message}</p>
          </div>
        </div>
      </div>
    `;
  }).join('');
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>My Living Announcements | Say It NOW</title>
      <style>
        @page {
          size: A4;
          margin: ${margins};
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 12px;
          line-height: 1.6;
          color: #1a1a1a;
          margin: 0;
          padding: 0;
          background: white;
        }
        
        .cover {
          height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          color: white;
          page-break-after: always;
        }
        
        .cover h1 {
          font-size: 3em;
          margin: 0;
          font-weight: 700;
        }
        
        .cover .subtitle {
          font-size: 1.2em;
          opacity: 0.8;
          margin-top: 0.5rem;
          letter-spacing: 0.2em;
        }
        
        .cover .count {
          margin-top: 2rem;
          padding: 1rem 2rem;
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 50px;
        }
        
        .page {
          padding: 2rem;
        }
        
        .page-break {
          page-break-before: always;
        }
        
        .page-header {
          text-align: right;
          font-size: 0.8em;
          color: #888;
          margin-bottom: 2rem;
        }
        
        .announcement {
          display: flex;
          gap: 2rem;
          align-items: flex-start;
        }
        
        .photo-section {
          flex: 0 0 200px;
        }
        
        .main-photo {
          width: 100%;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        .content-section {
          flex: 1;
        }
        
        .recipient {
          font-size: 1.8em;
          margin: 0 0 0.5rem 0;
          color: #1a1a1a;
        }
        
        .date {
          font-size: 0.9em;
          color: #888;
          margin-bottom: 1.5rem;
        }
        
        .message {
          font-size: 1.1em;
          line-height: 1.8;
          white-space: pre-wrap;
        }
        
        .footer {
          position: fixed;
          bottom: 1cm;
          left: 0;
          right: 0;
          text-align: center;
          font-size: 0.75em;
          color: #999;
        }
        
        ${isPremium ? `
          .cover {
            background: linear-gradient(135deg, #4a3728 0%, #6b5344 100%);
          }
          
          .announcement {
            border-left: 3px solid #d4a574;
            padding-left: 2rem;
          }
          
          .recipient {
            color: #4a3728;
          }
        ` : ''}
        
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="cover">
        <h1>Say It NOW</h1>
        <p class="subtitle">While They're Still Here</p>
        <div class="count">
          ${announcements.length} Living ${announcements.length === 1 ? 'Announcement' : 'Announcements'}
        </div>
        <p style="margin-top: 2rem; opacity: 0.6;">
          ${new Date().toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
        </p>
      </div>
      
      ${announcementPages}
      
      <div class="footer">
        Created with Say It NOW — While They're Still Here
      </div>
    </body>
    </html>
  `;
}
